import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { ProductImage } from './ProductImage.js';
import { produktbild } from '../assets/product-images.js';
import type {
  CatalogSelectionResult,
  CatalogTable,
  MaterialOption,
  ProductOption,
  SelectedPosition,
} from '../api/types.js';

/**
 * Gefuehrte Katalogauswahl, der primaere Weg zum Artikel.
 *
 * ```text
 * Bauform -> Ausfuehrung -> Werkstoff -> Katalogtabelle -> Mengen -> uebernehmen
 * ```
 *
 * **In der Katalogtabelle steht kein Preis.** Die Auswahl beantwortet, *was* der
 * Kunde moechte. Preise entstehen erst in der Vorschau und stammen dort aus der
 * Engine, nicht aus dieser Ansicht.
 *
 * Alle Schaltflaechen entstehen aus Stammdaten. Nichts ist hier fest verdrahtet:
 * Ein neuer Artikeltyp oder Werkstoff erscheint, sobald es Artikel dazu gibt.
 */
export function GuidedSelection({
  onTakeOver,
}: {
  onTakeOver: (positions: readonly SelectedPosition[]) => void;
}): React.JSX.Element {
  const [products, setProducts] = useState<readonly ProductOption[]>([]);
  const [shape, setShape] = useState<string | null>(null);
  const [product, setProduct] = useState<ProductOption | null>(null);
  const [materials, setMaterials] = useState<readonly MaterialOption[]>([]);
  const [material, setMaterial] = useState<MaterialOption | null>(null);
  const [table, setTable] = useState<CatalogTable | null>(null);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [rejected, setRejected] = useState<CatalogSelectionResult['rejected']>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ items: ProductOption[] }>('/api/catalog/products')
      .then((r) => setProducts(r.items))
      .catch((e: Error) => setError(e.message));
  }, []);

  // Bauformen: die Kurzform, unter der mehrere Ausfuehrungen haengen.
  const shapes = [...new Set(products.map((p) => p.label))].sort((a, b) =>
    a.localeCompare(b, 'de'),
  );
  const variantsOfShape = products.filter((p) => p.label === shape);

  function chooseShape(next: string): void {
    setShape(next);
    setProduct(null);
    setMaterials([]);
    setMaterial(null);
    setTable(null);
    setQuantities({});
    setRejected([]);
  }

  async function chooseProduct(next: ProductOption): Promise<void> {
    setProduct(next);
    setMaterial(null);
    setTable(null);
    setQuantities({});
    setRejected([]);
    const r = await api.get<{ items: MaterialOption[] }>(
      `/api/catalog/products/${next.productId}/materials`,
    );
    setMaterials(r.items);
  }

  async function chooseMaterial(next: MaterialOption): Promise<void> {
    if (product === null) return;
    setMaterial(next);
    setQuantities({});
    setRejected([]);
    setBusy(true);
    try {
      const r = await api.get<CatalogTable>(
        `/api/catalog/table?productId=${product.productId}&materialCode=${encodeURIComponent(next.code)}`,
      );
      setTable(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function takeOver(): Promise<void> {
    if (table === null) return;
    const items = Object.entries(quantities)
      .filter(([, value]) => value.trim() !== '')
      .map(([variantId, quantity]) => ({ variantId, quantity }));
    if (items.length === 0) return;

    setBusy(true);
    try {
      const result = await api.post<CatalogSelectionResult>('/api/catalog/selection', {
        materialCode: table.materialCode,
        items,
      });
      setRejected(result.rejected);
      onTakeOver(result.positions);
      // Uebernommene Mengen werden geleert, abgelehnte bleiben zur Korrektur stehen.
      const abgelehnt = new Set(result.rejected.map((r) => r.variantId));
      setQuantities((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([id]) => abgelehnt.has(id))),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const eingegeben = Object.values(quantities).filter((v) => v.trim() !== '').length;
  // Spaltenkoepfe aus allen Zeilen: Welche Masse es gibt, sagt die Quelle -- je
  // Produkttyp verschieden (G-ST-P/GR fuehrt d3, G-ST-P/S nicht). Die Vereinigung
  // statt nur der ersten Zeile, damit eine Zeile ohne ein Mass nicht die Spalte fuer
  // alle uebrigen unterdrueckt. Sortiert nach Schluessel wie die Masse je Zeile vom
  // Server: d1, d2, d3, s1, s2.
  const measureKeys = [
    ...new Set((table?.rows ?? []).flatMap((r) => r.measures.map((m) => m.key))),
  ].sort((a, b) => a.localeCompare(b));
  const measureLabels = new Map(
    (table?.rows ?? []).flatMap((r) => r.measures.map((m) => [m.key, m.label] as const)),
  );
  // G-ST-P/GR fuehrt mit d3 eine Spalte mehr als jede andere Familie. Die vollen
  // Merkmalsbezeichnungen ("Innendurchmesser d1" usw.) sprengen bei neun Spalten
  // die verfuegbare Breite neben der Seitenleiste und schneiden die Mengenspalte
  // ab. Ausschliesslich fuer GR deshalb kompakte Kopfzeilen; die volle Bezeichnung
  // bleibt als Tooltip/zugaengliche Beschriftung ueber <abbr> erhalten. Andere
  // Produkttypen sind unveraendert.
  const isGr = table?.imageRef === 'g-st-gr';
  const GR_MEASURE_HEADERS: Readonly<Record<string, string>> = {
    d1: 'd1 innen',
    d2: 'd2 außen',
    d3: 'd3 Profil',
    s1: 's1',
    s2: 's2',
  };

  return (
    <div className="guided">
      {error !== null && <p role="alert">{error}</p>}

      <Step number={1} title="Bauform">
        <div className="choices">
          {shapes.map((s) => (
            <button
              key={s}
              type="button"
              className={s === shape ? 'choice selected' : 'choice'}
              onClick={() => chooseShape(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </Step>

      {shape !== null && (
        <Step number={2} title="Ausfuehrung">
          <div className="choices">
            {variantsOfShape.map((p) => (
              <button
                key={p.productId}
                type="button"
                className={
                  p.productId === product?.productId ? 'choice selected' : 'choice'
                }
                onClick={() => void chooseProduct(p)}
              >
                <span className="choice-main">{p.standardLabel ?? p.typeLabel}</span>
                {/* Der Code bleibt sichtbar: Zwei Ausfuehrungen tragen im Stamm
                    denselben Klartext, und ein Unterschied wird nicht erfunden. */}
                <span className="choice-note">
                  Code {p.code} · {p.articleCount} Artikel
                </span>
              </button>
            ))}
          </div>
        </Step>
      )}

      {product !== null && (
        <Step number={3} title="Werkstoff">
          <div className="choices">
            {materials.map((m) => (
              <button
                key={m.code}
                type="button"
                className={m.code === material?.code ? 'choice selected' : 'choice'}
                onClick={() => void chooseMaterial(m)}
              >
                <span className="choice-main">{m.label}</span>
                <span className="choice-note">
                  Code {m.code} · {m.articleCount} Artikel
                </span>
              </button>
            ))}
          </div>
        </Step>
      )}

      {table !== null && (
        <Step number={4} title="Abmessungen und Mengen">
          <p className="lead">
            {table.productLabel}
            {table.standardLabel !== null && ` · ${table.standardLabel}`} ·{' '}
            {table.materialLabel}
          </p>
          <p className="note">
            Diese Tabelle zeigt keine Preise. Mehrere Mengen lassen sich in einem Schritt
            eintragen.
          </p>
          {/*
            Die Schnittdarstellung steht einmal gross ueber der Tabelle, nicht als
            Symbol in jeder Zeile: Sie beschriftet d1, d2, s1 und s2 (bei G-ST-P/GR
            zusaetzlich d3) und erklaert damit unmittelbar die Spalten darunter.
          */}
          {/* Ohne Bild entfaellt der ganze Block. Ein leerer Rahmen mit
              Bildunterschrift waere schlechter als gar nichts. */}
          {produktbild(table.imageRef) !== null && (
            <figure className="produktbild-rahmen">
              <ProductImage
                imageRef={table.imageRef}
                variant="gross"
                label={table.productLabel}
              />
              <figcaption>
                Schnittdarstellung {table.productLabel}. Die Bezeichnungen entsprechen den
                Spalten der Tabelle.
              </figcaption>
            </figure>
          )}
          <div className="table-scroll">
            <table className={isGr ? 'gr-table' : undefined}>
              <thead>
                <tr>
                  <th scope="col">Nennweite</th>
                  <th scope="col">{isGr ? 'PN' : 'Druckstufe'}</th>
                  {measureKeys.map((k) => {
                    const voll = measureLabels.get(k) ?? k;
                    const kurz = isGr ? (GR_MEASURE_HEADERS[k] ?? voll) : voll;
                    return (
                      <th scope="col" key={k}>
                        {isGr ? <abbr title={voll}>{kurz}</abbr> : kurz}
                      </th>
                    );
                  })}
                  <th scope="col">Artikelnummer</th>
                  <th scope="col">Menge</th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => {
                  const byKey = new Map(row.measures.map((m) => [m.key, m]));
                  return (
                    <tr key={row.variantId}>
                      <th scope="row">{row.sizeLabel}</th>
                      <td>{row.pressureLabel ?? '–'}</td>
                      {measureKeys.map((k) => {
                        const m = byKey.get(k);
                        return (
                          <td key={k} className="num">
                            {m === undefined ? '–' : `${m.value}${m.unit ?? ''}`}
                          </td>
                        );
                      })}
                      <td>
                        {row.article === null ? (
                          // Keine erfundene Nummer: Diese Kombination ist im
                          // Artikelstamm nicht gefuehrt.
                          <span className="muted">nicht gefuehrt</span>
                        ) : (
                          <code>{row.article.articleNumber}</code>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="qty"
                          aria-label={`Menge ${row.sizeLabel}`}
                          disabled={row.article === null}
                          value={quantities[row.variantId] ?? ''}
                          onChange={(e) =>
                            setQuantities((prev) => ({
                              ...prev,
                              [row.variantId]: e.target.value,
                            }))
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/*
            Sticky Aktionsleiste: Bei langen Tabellen (z. B. DN 15 bis DN 2000)
            bleibt "Auswahl uebernehmen" beim Scrollen erreichbar, statt erst
            unterhalb der kompletten Tabelle zu stehen. Dasselbe
            `position: sticky`-Prinzip wie bei den Spaltenkoepfen und der
            Warenkorb-Seitenleiste, siehe theme.css.
          */}
          <div className="table-actions">
            {rejected.length > 0 && (
              <ul className="rejected" role="alert">
                {rejected.map((r) => (
                  <li key={r.variantId}>{r.detail}</li>
                ))}
              </ul>
            )}

            <button
              type="button"
              className="primary"
              disabled={eingegeben === 0 || busy}
              onClick={() => void takeOver()}
            >
              Auswahl uebernehmen
              {eingegeben > 0 && ` (${eingegeben})`}
            </button>
          </div>
        </Step>
      )}
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="card step">
      <h3>
        <span className="step-number">{number}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}
